import { endOfMonth, format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { moneyFormat } from 'helpers/moneyFormat';
import { getGuideStatusLabel } from 'helpers/guideStatus';
import { GuideOrder, guideOrderParams, nextGuideOrder } from 'helpers/guideSort';
import { useApp } from 'hooks/app';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { api } from 'services/api';
import { Guide, GuideSummary } from 'types/guide';
import { Paginated } from 'types/paginated';
import { toast } from 'react-toastify';

export interface ReportGuideQueryParams {
  initial_date: Date;
  final_date: Date;
  date_filter: 'created_at' | 'appointment_date';
  patient_name: string;
  doctor_name: string;
  marker_id: number | null;
  status: string;
}

export function useFetchReportGuides() {
  const [order, setOrder] = useState<GuideOrder | null>(null);
  const { isOpenedMenu, handleOpenMenu } = useApp();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [summary, setSummary] = useState<GuideSummary | null>(null);
  const [filter, setFilter] = useState<ReportGuideQueryParams>({
    initial_date: startOfMonth(new Date()),
    final_date: endOfMonth(new Date()),
    date_filter: 'appointment_date',
    patient_name: '',
    doctor_name: '',
    marker_id: null,
    // o relatório considera somente guias concluídas
    status: 'completed',
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [exporting, setExporting] = useState(false);
  // filtros e ordem da última busca: a exportação deve bater com o que a tela está mostrando
  const lastQuery = useRef<{ filter: ReportGuideQueryParams; order: GuideOrder | null }>({ filter, order });

  function handleChange(index: keyof ReportGuideQueryParams, value: any) {
    setFilter({
      ...filter,
      [index]: value,
    });
  }

  const formatGuideData = useMemo(
    () =>
      (guides: Guide[]): Guide[] => {
        return guides.map(guide => ({
          ...guide,

          formattedMarkerName: guide.marker.name,
          formattedDoctorName: guide.doctor.name,
          formattedPatientName: guide.patient.name,
          formattedProcedureName: guide.procedure.name,
          formattedValueMarker: moneyFormat(guide.value_marker),
          formattedValueProcedure: moneyFormat(guide.value_procedure),
          formattedValueDiscount: moneyFormat(guide.value_discount),
          formattedValueTotal: moneyFormat(guide.value_total),
          formattedAppointmentDate: format(guide.appointment_date, 'P', { locale: ptBR }),
          formattedCreatedAt: format(guide.created_at, 'Pp', { locale: ptBR }),
          formattedValueCost: moneyFormat(guide.value_cost ?? 0),
          formattedPaymentMethods:
            (guide.payment_methods || [])
              .filter(item => item.active)
              .map(item => item.payment_method?.name)
              .filter(Boolean)
              .join(', ') || '-',
          formattedStatus: getGuideStatusLabel(guide.status),
        }));
      },
    [ptBR],
  );

  function fetchGuides(nextPage = page, nextRows = rows, nextFilter = filter, nextOrder = order) {
    setLoading(true);
    lastQuery.current = { filter: nextFilter, order: nextOrder };
    if (isOpenedMenu) handleOpenMenu();

    api
      .get<Paginated<Guide[]> & { summary?: GuideSummary }>('/guides', {
        params: {
          ...nextFilter,
          page: nextPage + 1,
          rows: nextRows,
          date_filter: nextFilter.date_filter,
          ...guideOrderParams(nextOrder),
        },
      })
      .then(response => {
        const data = formatGuideData(response.data.items);
        setTotal(response.data.total);
        setSummary(response.data.summary ?? null);

        setGuides(data);
      })
      .catch(() => toast.error('Não foi possível carregar as guias'))
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchGuides(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchGuides(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchGuides(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const nextOrder = nextGuideOrder(index, order);
    if (!nextOrder) return;

    setOrder(nextOrder);
    setPage(0);
    fetchGuides(0, rows, filter, nextOrder);
  }

  function exportExcel() {
    const { filter: applied, order: appliedOrder } = lastQuery.current;
    const filters = Object.fromEntries(
      Object.entries({
        initial_date: applied.initial_date,
        final_date: applied.final_date,
        date_filter: applied.date_filter,
        status: applied.status,
        patient_name: applied.patient_name,
        doctor_name: applied.doctor_name,
        marker_id: applied.marker_id,
        ...guideOrderParams(appliedOrder),
      }).filter(([, value]) => value !== '' && value !== null && value !== undefined),
    );

    setExporting(true);

    api
      .post('/excel-exports', { report: 'guides', filters }, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `guias_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Não foi possível exportar para Excel'))
      .finally(() => setExporting(false));
  }

  useEffect(() => {
    fetchGuides(0, rows, filter);
  }, []);

  return {
    filter,
    handleChange,
    loading,
    handleSearch,
    handleSort,
    exportExcel,
    exporting,
    order,
    guides,
    summary,
    anchorEl,
    setAnchorEl,
    selectedGuide,
    setSelectedGuide,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    rows,
    page,
    total,
  };
}
